import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import qapi from "views_shared/app/javascript/api";
import VideoPlayer from "./VideoPlayer";

// Feed/admin adapter around the shared VideoPlayer. A feed-post video carries only a public
// reference in the mat (no signed URL), so given an attachment id this fetches a freshly-signed
// Cloudinary delivery URL + metadata from AttachmentsController#show (authorised by
// Attachment::VideoPolicy) and hands the resolved attachment to VideoPlayer, which owns
// poster→tap, self-heal, transcript polling and seeking. Used by BOTH the feed card (frontend) and
// the admin review queue (admin), so it lives in views_shared (both import from here).
const VIDEO_PREVIEW_MAX_PX = 400;

function FeedVideoPlayer({ attachmentId }) {
  const [serverAttachment, setServerAttachment] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    // Unlike playback errors (which self-heal inside VideoPlayer), the INITIAL fetch has no recovery
    // path — a network blip or a 401/403/404 (post ghosted/deleted after render) leaves data
    // undefined / rejects. Flag it so we show a fallback instead of a spinner forever.
    qapi.attachments
      .find(attachmentId)
      .then(({ data }) => (data ? setServerAttachment(data) : setLoadError(true)))
      .catch(() => setLoadError(true));
  }, [attachmentId]);

  if (!serverAttachment) {
    if (loadError) {
      return (
        <div
          className="mt-2 card p-2 d-flex flex-row align-items-center gap-2 text-muted"
          style={{ width: "fit-content" }}
          data-testid="feed-video-unavailable"
        >
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          <small>Video unavailable</small>
        </div>
      );
    }
    return (
      <div
        className="mt-2 card p-2 d-flex flex-row align-items-center gap-2 text-muted"
        style={{ width: "fit-content" }}
        role="status"
      >
        <div className="spinner-border spinner-border-sm" aria-hidden="true" />
        <small>Loading video…</small>
      </div>
    );
  }

  return <VideoPlayer attachmentId={attachmentId} display={serverAttachment} maxPreviewPx={VIDEO_PREVIEW_MAX_PX} />;
}

FeedVideoPlayer.propTypes = {
  attachmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default FeedVideoPlayer;
